import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, NgModule } from '@angular/core';
import { BehaviorSubject, from, throwError, of, Observable } from 'rxjs';
import { map, mergeMap, catchError, finalize, first, retry, tap } from 'rxjs/operators';
import { HttpHeaders, HttpResponseBase, HttpResponse, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
function isJwtValid(jwt) {
  if (!jwt) {
    return false;
  }
  try {
    const expDate = getExpirationDateFromJwt(jwt); // in seconds
    const now = Date.now() / 1000; // Date.now() is in ms
    return expDate > now;
  } catch (e) {
    if (e instanceof Error) {
      console.error(`Error checking JWT validity: ${e.message}`);
    } else {
      console.error("Unexpected error occurred checking JWT validity");
    }
    return false;
  }
}
function decodeJwt(jwt) {
  return JSON.parse(atob(jwt.split(".")[1]));
}
function getExpirationDateFromJwt(jwt) {
  return decodeJwt(jwt).exp;
}
class GaAuthStore {
  accessToken$ = new BehaviorSubject(null);
  get accessToken() {
    return this.accessToken$.getValue();
  }
  get accessTokenExpiration$() {
    return this.accessToken$.asObservable().pipe(map(t => t ? getExpirationDateFromJwt(t) * 1000 : null));
  }
}
const GA_AUTH_CONFIG = new InjectionToken("GA_AUTH_CONFIG");
let GaAuthCookieService = /*#__PURE__*/(() => {
  class GaAuthCookieService {
    config;
    _prefix = "";
    constructor(config) {
      this.config = config;
      if (config.devOptions?.disableAuth) {
        return;
      }
      try {
        this._prefix = (new URL(config.gaAuthApiUrl).hostname.split(".")[0] || "") + ".";
      } catch {
        throw new Error(`Unable to determine prefix from provided URL: '${config.gaAuthApiUrl}'`);
      }
    }
    getCsrfTokenCookieName() {
      return this._prefix + "ga_csrf_token";
    }
    getRefreshTokenExpirationCookieName() {
      return this._prefix + "ga_refresh_token_expiration";
    }
    getIsReauthenticatingKey() {
      return this._prefix + "ga_reauthenticating";
    }
    clearTokenCookies() {
      const cookieNames = [this.getCsrfTokenCookieName(), this.getRefreshTokenExpirationCookieName()]; // should be moved eventually
      const cookies = document.cookie.split(";");
      for (const cookie of cookies) {
        const equalSignIndex = cookie.indexOf("=");
        const cookieName = equalSignIndex > -1 ? cookie.substring(0, equalSignIndex).trim() : cookie.trim();
        if (cookieNames.includes(cookieName)) {
          document.cookie = cookieName + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
        }
      }
    }
    static ɵfac = function GaAuthCookieService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GaAuthCookieService)(i0.ɵɵinject(GA_AUTH_CONFIG));
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: GaAuthCookieService,
      factory: GaAuthCookieService.ɵfac
    });
  }
  return GaAuthCookieService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let GaAuthApiAuthStore = /*#__PURE__*/(() => {
  class GaAuthApiAuthStore extends GaAuthStore {
    cookieService;
    cookies$ = new BehaviorSubject(document.cookie);
    refreshTokenExpirationDate$ = new BehaviorSubject(null);
    constructor(cookieService) {
      super();
      this.cookieService = cookieService;
      this.expirationDateFromCookie$.subscribe(this.refreshTokenExpirationDate$);
      this._reauthenticating$ = new BehaviorSubject(localStorage.getItem(this.cookieService.getIsReauthenticatingKey()) === "true");
    }
    _reauthenticating$;
    get reauthenticating$() {
      return this._reauthenticating$.asObservable();
    }
    get reauthenticating() {
      return localStorage.getItem(this.cookieService.getIsReauthenticatingKey()) === "true";
    }
    get csrfToken$() {
      return this.cookies$.asObservable().pipe(map(cookies => {
        const matches = cookies.split("; ").map(x => x.trim()).filter(x => x.startsWith(`${this.cookieService.getCsrfTokenCookieName()}=`));
        if (!matches.length) return null;
        return decodeURIComponent(matches[0].split("=")[1]);
      }));
    }
    get expirationDateFromCookie$() {
      return this.cookies$.asObservable().pipe(map(cookies => {
        const matches = cookies.split(";").map(x => x.trim()).filter(x => x.startsWith(`${this.cookieService.getRefreshTokenExpirationCookieName()}=`));
        if (!matches.length) return null;
        return parseInt(matches[0].split("=")[1]);
      }));
    }
    setReauthenticating(value) {
      localStorage.setItem(this.cookieService.getIsReauthenticatingKey(), `${value}`);
      this._reauthenticating$.next(value);
    }
    static ɵfac = function GaAuthApiAuthStore_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GaAuthApiAuthStore)(i0.ɵɵinject(GaAuthCookieService));
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: GaAuthApiAuthStore,
      factory: GaAuthApiAuthStore.ɵfac
    });
  }
  return GaAuthApiAuthStore;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * This class allows the cas api client to access the auth service through the api base class since you can't directly inject the auth service
 */
let GaAuthApiConfig = /*#__PURE__*/(() => {
  class GaAuthApiConfig {
    authStore;
    _csrfToken$ = new BehaviorSubject(null);
    constructor(authStore) {
      this.authStore = authStore;
      this.authStore.csrfToken$.subscribe(this._csrfToken$);
    }
    get csrfToken() {
      return this._csrfToken$.getValue();
    }
    static ɵfac = function GaAuthApiConfig_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GaAuthApiConfig)(i0.ɵɵinject(GaAuthApiAuthStore));
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: GaAuthApiConfig,
      factory: GaAuthApiConfig.ɵfac
    });
  }
  return GaAuthApiConfig;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

//----------------------
// <auto-generated>
//     Generated using the NSwag toolchain v13.18.2.0 (NJsonSchema v10.8.0.0 (Newtonsoft.Json v13.0.0.0)) (http://NSwag.org)
// </auto-generated>
//----------------------
/* tslint:disable */
/* eslint-disable */
// ReSharper disable InconsistentNaming
var GaAuthApiClient;
(function (GaAuthApiClient) {
  GaAuthApiClient.GA_AUTH_API_BASE_URL = new InjectionToken("GA_AUTH_API_BASE_URL");
  class GaAuthApiClientBase {
    apiConfig;
    constructor(apiConfig) {
      this.apiConfig = apiConfig;
    }
    transformOptions(options) {
      const csrfToken = this.apiConfig.csrfToken;
      if (csrfToken != null) {
        options.headers = options.headers.append("X-CSRF-Token", csrfToken);
      }
      return Promise.resolve(options);
    }
  }
  GaAuthApiClient.GaAuthApiClientBase = GaAuthApiClientBase;
  let AccessTokenService = /*#__PURE__*/(() => {
    class AccessTokenService extends GaAuthApiClientBase {
      http;
      baseUrl;
      jsonParseReviver = undefined;
      constructor(configuration, http, baseUrl) {
        super(configuration);
        this.http = http;
        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
      }
      getAccessTokenForWeb(forWebRequest, httpContext) {
        let url_ = this.baseUrl + "/auth/web/AccessToken";
        url_ = url_.replace(/[?&]$/, "");
        const content_ = JSON.stringify(forWebRequest);
        let options_ = {
          body: content_,
          observe: "response",
          responseType: "blob",
          withCredentials: true,
          context: httpContext,
          headers: new HttpHeaders({
            "Content-Type": "application/json",
            Accept: "application/json"
          })
        };
        return from(this.transformOptions(options_)).pipe(mergeMap(transformedOptions_ => {
          return this.http.request("post", url_, transformedOptions_);
        })).pipe(mergeMap(response_ => {
          return this.processGetAccessTokenForWeb(response_);
        })).pipe(catchError(response_ => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAccessTokenForWeb(response_);
            } catch (e) {
              return throwError(e);
            }
          } else return throwError(response_);
        }));
      }
      processGetAccessTokenForWeb(response) {
        const status = response.status;
        const responseBlob = response instanceof HttpResponse ? response.body : response.error instanceof Blob ? response.error : undefined;
        let _headers = {};
        if (response.headers) {
          for (let key of response.headers.keys()) {
            _headers[key] = response.headers.get(key);
          }
        }
        if (status === 200) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result200 = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = GetAccessTokenForWebResponse.fromJS(resultData200);
            return of(result200);
          }));
        } else if (status === 401) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result401 = null;
            let resultData401 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result401 = CentralAuthErrorResponse.fromJS(resultData401);
            return throwException("A server side error occurred.", status, _responseText, _headers, result401);
          }));
        } else if (status === 403) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result403 = null;
            let resultData403 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result403 = CentralAuthErrorResponse.fromJS(resultData403);
            return throwException("A server side error occurred.", status, _responseText, _headers, result403);
          }));
        } else if (status === 500) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result500 = null;
            let resultData500 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result500 = CentralAuthErrorResponse.fromJS(resultData500);
            return throwException("A server side error occurred.", status, _responseText, _headers, result500);
          }));
        } else if (status !== 200 && status !== 204) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
          }));
        }
        return of(null);
      }
      ackWebAccessToken(request, httpContext) {
        let url_ = this.baseUrl + "/auth/web/Ack";
        url_ = url_.replace(/[?&]$/, "");
        const content_ = JSON.stringify(request);
        let options_ = {
          body: content_,
          observe: "response",
          responseType: "blob",
          withCredentials: true,
          context: httpContext,
          headers: new HttpHeaders({
            "Content-Type": "application/json"
          })
        };
        return from(this.transformOptions(options_)).pipe(mergeMap(transformedOptions_ => {
          return this.http.request("post", url_, transformedOptions_);
        })).pipe(mergeMap(response_ => {
          return this.processAckWebAccessToken(response_);
        })).pipe(catchError(response_ => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processAckWebAccessToken(response_);
            } catch (e) {
              return throwError(e);
            }
          } else return throwError(response_);
        }));
      }
      processAckWebAccessToken(response) {
        const status = response.status;
        const responseBlob = response instanceof HttpResponse ? response.body : response.error instanceof Blob ? response.error : undefined;
        let _headers = {};
        if (response.headers) {
          for (let key of response.headers.keys()) {
            _headers[key] = response.headers.get(key);
          }
        }
        if (status === 204) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            return of(null);
          }));
        } else if (status !== 200 && status !== 204) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
          }));
        }
        return of(null);
      }
      getAccessTokenForM2M(request, httpContext) {
        let url_ = this.baseUrl + "/auth/m2m/AccessToken";
        url_ = url_.replace(/[?&]$/, "");
        const content_ = JSON.stringify(request);
        let options_ = {
          body: content_,
          observe: "response",
          responseType: "blob",
          withCredentials: true,
          context: httpContext,
          headers: new HttpHeaders({
            "Content-Type": "application/json",
            Accept: "application/json"
          })
        };
        return from(this.transformOptions(options_)).pipe(mergeMap(transformedOptions_ => {
          return this.http.request("post", url_, transformedOptions_);
        })).pipe(mergeMap(response_ => {
          return this.processGetAccessTokenForM2M(response_);
        })).pipe(catchError(response_ => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAccessTokenForM2M(response_);
            } catch (e) {
              return throwError(e);
            }
          } else return throwError(response_);
        }));
      }
      processGetAccessTokenForM2M(response) {
        const status = response.status;
        const responseBlob = response instanceof HttpResponse ? response.body : response.error instanceof Blob ? response.error : undefined;
        let _headers = {};
        if (response.headers) {
          for (let key of response.headers.keys()) {
            _headers[key] = response.headers.get(key);
          }
        }
        if (status === 200) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result200 = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = GetAccessTokenForM2MResponse.fromJS(resultData200);
            return of(result200);
          }));
        } else if (status === 401) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result401 = null;
            let resultData401 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result401 = CentralAuthErrorResponse.fromJS(resultData401);
            return throwException("A server side error occurred.", status, _responseText, _headers, result401);
          }));
        } else if (status === 403) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result403 = null;
            let resultData403 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result403 = CentralAuthErrorResponse.fromJS(resultData403);
            return throwException("A server side error occurred.", status, _responseText, _headers, result403);
          }));
        } else if (status === 500) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result500 = null;
            let resultData500 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result500 = CentralAuthErrorResponse.fromJS(resultData500);
            return throwException("A server side error occurred.", status, _responseText, _headers, result500);
          }));
        } else if (status !== 200 && status !== 204) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
          }));
        }
        return of(null);
      }
      static ɵfac = function AccessTokenService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AccessTokenService)(i0.ɵɵinject(GaAuthApiConfig), i0.ɵɵinject(HttpClient), i0.ɵɵinject(GaAuthApiClient.GA_AUTH_API_BASE_URL, 8));
      };
      static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AccessTokenService,
        factory: AccessTokenService.ɵfac
      });
    }
    return AccessTokenService;
  })();
  GaAuthApiClient.AccessTokenService = AccessTokenService;
  let LogoutService = /*#__PURE__*/(() => {
    class LogoutService extends GaAuthApiClientBase {
      http;
      baseUrl;
      jsonParseReviver = undefined;
      constructor(configuration, http, baseUrl) {
        super(configuration);
        this.http = http;
        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
      }
      logout(input, httpContext) {
        let url_ = this.baseUrl + "/auth/Logout";
        url_ = url_.replace(/[?&]$/, "");
        const content_ = JSON.stringify(input);
        let options_ = {
          body: content_,
          observe: "response",
          responseType: "blob",
          withCredentials: true,
          context: httpContext,
          headers: new HttpHeaders({
            "Content-Type": "application/json"
          })
        };
        return from(this.transformOptions(options_)).pipe(mergeMap(transformedOptions_ => {
          return this.http.request("post", url_, transformedOptions_);
        })).pipe(mergeMap(response_ => {
          return this.processLogout(response_);
        })).pipe(catchError(response_ => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processLogout(response_);
            } catch (e) {
              return throwError(e);
            }
          } else return throwError(response_);
        }));
      }
      processLogout(response) {
        const status = response.status;
        const responseBlob = response instanceof HttpResponse ? response.body : response.error instanceof Blob ? response.error : undefined;
        let _headers = {};
        if (response.headers) {
          for (let key of response.headers.keys()) {
            _headers[key] = response.headers.get(key);
          }
        }
        if (status === 204) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            return of(null);
          }));
        } else if (status === 401) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result401 = null;
            let resultData401 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result401 = CentralAuthErrorResponse.fromJS(resultData401);
            return throwException("A server side error occurred.", status, _responseText, _headers, result401);
          }));
        } else if (status === 403) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result403 = null;
            let resultData403 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result403 = CentralAuthErrorResponse.fromJS(resultData403);
            return throwException("A server side error occurred.", status, _responseText, _headers, result403);
          }));
        } else if (status === 500) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result500 = null;
            let resultData500 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result500 = CentralAuthErrorResponse.fromJS(resultData500);
            return throwException("A server side error occurred.", status, _responseText, _headers, result500);
          }));
        } else if (status !== 200 && status !== 204) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
          }));
        }
        return of(null);
      }
      static ɵfac = function LogoutService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LogoutService)(i0.ɵɵinject(GaAuthApiConfig), i0.ɵɵinject(HttpClient), i0.ɵɵinject(GaAuthApiClient.GA_AUTH_API_BASE_URL, 8));
      };
      static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LogoutService,
        factory: LogoutService.ɵfac
      });
    }
    return LogoutService;
  })();
  GaAuthApiClient.LogoutService = LogoutService;
  let PublicKeysService = /*#__PURE__*/(() => {
    class PublicKeysService extends GaAuthApiClientBase {
      http;
      baseUrl;
      jsonParseReviver = undefined;
      constructor(configuration, http, baseUrl) {
        super(configuration);
        this.http = http;
        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
      }
      getPublicKeys(httpContext) {
        let url_ = this.baseUrl + "/auth/PublicKeys";
        url_ = url_.replace(/[?&]$/, "");
        let options_ = {
          observe: "response",
          responseType: "blob",
          withCredentials: true,
          context: httpContext,
          headers: new HttpHeaders({
            Accept: "application/json"
          })
        };
        return from(this.transformOptions(options_)).pipe(mergeMap(transformedOptions_ => {
          return this.http.request("get", url_, transformedOptions_);
        })).pipe(mergeMap(response_ => {
          return this.processGetPublicKeys(response_);
        })).pipe(catchError(response_ => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetPublicKeys(response_);
            } catch (e) {
              return throwError(e);
            }
          } else return throwError(response_);
        }));
      }
      processGetPublicKeys(response) {
        const status = response.status;
        const responseBlob = response instanceof HttpResponse ? response.body : response.error instanceof Blob ? response.error : undefined;
        let _headers = {};
        if (response.headers) {
          for (let key of response.headers.keys()) {
            _headers[key] = response.headers.get(key);
          }
        }
        if (status === 200) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result200 = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = JsonWebKeySet.fromJS(resultData200);
            return of(result200);
          }));
        } else if (status !== 200 && status !== 204) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
          }));
        }
        return of(null);
      }
      static ɵfac = function PublicKeysService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicKeysService)(i0.ɵɵinject(GaAuthApiConfig), i0.ɵɵinject(HttpClient), i0.ɵɵinject(GaAuthApiClient.GA_AUTH_API_BASE_URL, 8));
      };
      static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicKeysService,
        factory: PublicKeysService.ɵfac
      });
    }
    return PublicKeysService;
  })();
  GaAuthApiClient.PublicKeysService = PublicKeysService;
  let SamlRedirectService = /*#__PURE__*/(() => {
    class SamlRedirectService extends GaAuthApiClientBase {
      http;
      baseUrl;
      jsonParseReviver = undefined;
      constructor(configuration, http, baseUrl) {
        super(configuration);
        this.http = http;
        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
      }
      handleSamlRedirect(samlResponse, httpContext) {
        let url_ = this.baseUrl + "/auth/SamlRedirect";
        url_ = url_.replace(/[?&]$/, "");
        let content_ = "";
        if (samlResponse !== undefined) content_ += encodeURIComponent("SamlResponse") + "=" + encodeURIComponent("" + samlResponse) + "&";
        content_ = content_.replace(/&$/, "");
        let options_ = {
          body: content_,
          observe: "response",
          responseType: "blob",
          withCredentials: true,
          context: httpContext,
          headers: new HttpHeaders({
            "Content-Type": "application/x-www-form-urlencoded"
          })
        };
        return from(this.transformOptions(options_)).pipe(mergeMap(transformedOptions_ => {
          return this.http.request("post", url_, transformedOptions_);
        })).pipe(mergeMap(response_ => {
          return this.processHandleSamlRedirect(response_);
        })).pipe(catchError(response_ => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processHandleSamlRedirect(response_);
            } catch (e) {
              return throwError(e);
            }
          } else return throwError(response_);
        }));
      }
      processHandleSamlRedirect(response) {
        const status = response.status;
        const responseBlob = response instanceof HttpResponse ? response.body : response.error instanceof Blob ? response.error : undefined;
        let _headers = {};
        if (response.headers) {
          for (let key of response.headers.keys()) {
            _headers[key] = response.headers.get(key);
          }
        }
        if (status === 302) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            return throwException("A server side error occurred.", status, _responseText, _headers);
          }));
        } else if (status === 401) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result401 = null;
            let resultData401 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result401 = CentralAuthErrorResponse.fromJS(resultData401);
            return throwException("A server side error occurred.", status, _responseText, _headers, result401);
          }));
        } else if (status === 500) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result500 = null;
            let resultData500 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result500 = CentralAuthErrorResponse.fromJS(resultData500);
            return throwException("A server side error occurred.", status, _responseText, _headers, result500);
          }));
        } else if (status !== 200 && status !== 204) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
          }));
        }
        return of(null);
      }
      static ɵfac = function SamlRedirectService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SamlRedirectService)(i0.ɵɵinject(GaAuthApiConfig), i0.ɵɵinject(HttpClient), i0.ɵɵinject(GaAuthApiClient.GA_AUTH_API_BASE_URL, 8));
      };
      static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SamlRedirectService,
        factory: SamlRedirectService.ɵfac
      });
    }
    return SamlRedirectService;
  })();
  GaAuthApiClient.SamlRedirectService = SamlRedirectService;
  class GetAccessTokenForWebResponse {
    accessToken;
    ackToken;
    constructor(data) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) this[property] = data[property];
        }
      }
    }
    init(_data) {
      if (_data) {
        this.accessToken = _data["accessToken"];
        this.ackToken = _data["ackToken"];
      }
    }
    static fromJS(data) {
      data = typeof data === "object" ? data : {};
      let result = new GetAccessTokenForWebResponse();
      result.init(data);
      return result;
    }
    toJSON(data) {
      data = typeof data === "object" ? data : {};
      data["accessToken"] = this.accessToken;
      data["ackToken"] = this.ackToken;
      return data;
    }
  }
  GaAuthApiClient.GetAccessTokenForWebResponse = GetAccessTokenForWebResponse;
  class CentralAuthErrorResponse {
    message;
    constructor(data) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) this[property] = data[property];
        }
      }
    }
    init(_data) {
      if (_data) {
        this.message = _data["message"];
      }
    }
    static fromJS(data) {
      data = typeof data === "object" ? data : {};
      let result = new CentralAuthErrorResponse();
      result.init(data);
      return result;
    }
    toJSON(data) {
      data = typeof data === "object" ? data : {};
      data["message"] = this.message;
      return data;
    }
  }
  GaAuthApiClient.CentralAuthErrorResponse = CentralAuthErrorResponse;
  class GetAccessTokenForWebRequest {
    requestedRoles;
    constructor(data) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) this[property] = data[property];
        }
      }
      if (!data) {
        this.requestedRoles = [];
      }
    }
    init(_data) {
      if (_data) {
        if (Array.isArray(_data["requestedRoles"])) {
          this.requestedRoles = [];
          for (let item of _data["requestedRoles"]) this.requestedRoles.push(item);
        }
      }
    }
    static fromJS(data) {
      data = typeof data === "object" ? data : {};
      let result = new GetAccessTokenForWebRequest();
      result.init(data);
      return result;
    }
    toJSON(data) {
      data = typeof data === "object" ? data : {};
      if (Array.isArray(this.requestedRoles)) {
        data["requestedRoles"] = [];
        for (let item of this.requestedRoles) data["requestedRoles"].push(item);
      }
      return data;
    }
  }
  GaAuthApiClient.GetAccessTokenForWebRequest = GetAccessTokenForWebRequest;
  class AckWebAccessTokenRequest {
    ackToken;
    constructor(data) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) this[property] = data[property];
        }
      }
    }
    init(_data) {
      if (_data) {
        this.ackToken = _data["ackToken"];
      }
    }
    static fromJS(data) {
      data = typeof data === "object" ? data : {};
      let result = new AckWebAccessTokenRequest();
      result.init(data);
      return result;
    }
    toJSON(data) {
      data = typeof data === "object" ? data : {};
      data["ackToken"] = this.ackToken;
      return data;
    }
  }
  GaAuthApiClient.AckWebAccessTokenRequest = AckWebAccessTokenRequest;
  class GetAccessTokenForM2MResponse {
    accessToken;
    expiresAt;
    constructor(data) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) this[property] = data[property];
        }
      }
    }
    init(_data) {
      if (_data) {
        this.accessToken = _data["accessToken"];
        this.expiresAt = _data["expiresAt"];
      }
    }
    static fromJS(data) {
      data = typeof data === "object" ? data : {};
      let result = new GetAccessTokenForM2MResponse();
      result.init(data);
      return result;
    }
    toJSON(data) {
      data = typeof data === "object" ? data : {};
      data["accessToken"] = this.accessToken;
      data["expiresAt"] = this.expiresAt;
      return data;
    }
  }
  GaAuthApiClient.GetAccessTokenForM2MResponse = GetAccessTokenForM2MResponse;
  class GetAccessTokenForM2MRequest {
    audience;
    clientId;
    clientSecret;
    requestedRoles;
    constructor(data) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) this[property] = data[property];
        }
      }
      if (!data) {
        this.requestedRoles = [];
      }
    }
    init(_data) {
      if (_data) {
        this.audience = _data["audience"];
        this.clientId = _data["clientId"];
        this.clientSecret = _data["clientSecret"];
        if (Array.isArray(_data["requestedRoles"])) {
          this.requestedRoles = [];
          for (let item of _data["requestedRoles"]) this.requestedRoles.push(item);
        }
      }
    }
    static fromJS(data) {
      data = typeof data === "object" ? data : {};
      let result = new GetAccessTokenForM2MRequest();
      result.init(data);
      return result;
    }
    toJSON(data) {
      data = typeof data === "object" ? data : {};
      data["audience"] = this.audience;
      data["clientId"] = this.clientId;
      data["clientSecret"] = this.clientSecret;
      if (Array.isArray(this.requestedRoles)) {
        data["requestedRoles"] = [];
        for (let item of this.requestedRoles) data["requestedRoles"].push(item);
      }
      return data;
    }
  }
  GaAuthApiClient.GetAccessTokenForM2MRequest = GetAccessTokenForM2MRequest;
  class LogoutInput {
    logoutOfAllSessions;
    constructor(data) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) this[property] = data[property];
        }
      }
    }
    init(_data) {
      if (_data) {
        this.logoutOfAllSessions = _data["logoutOfAllSessions"];
      }
    }
    static fromJS(data) {
      data = typeof data === "object" ? data : {};
      let result = new LogoutInput();
      result.init(data);
      return result;
    }
    toJSON(data) {
      data = typeof data === "object" ? data : {};
      data["logoutOfAllSessions"] = this.logoutOfAllSessions;
      return data;
    }
  }
  GaAuthApiClient.LogoutInput = LogoutInput;
  class JsonWebKeySet {
    keys;
    constructor(data) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) this[property] = data[property];
        }
      }
      if (!data) {
        this.keys = [];
      }
    }
    init(_data) {
      if (_data) {
        if (Array.isArray(_data["keys"])) {
          this.keys = [];
          for (let item of _data["keys"]) this.keys.push(JsonWebKey.fromJS(item));
        }
      }
    }
    static fromJS(data) {
      data = typeof data === "object" ? data : {};
      let result = new JsonWebKeySet();
      result.init(data);
      return result;
    }
    toJSON(data) {
      data = typeof data === "object" ? data : {};
      if (Array.isArray(this.keys)) {
        data["keys"] = [];
        for (let item of this.keys) data["keys"].push(item.toJSON());
      }
      return data;
    }
  }
  GaAuthApiClient.JsonWebKeySet = JsonWebKeySet;
  class JsonWebKey {
    kty;
    use;
    kid;
    x5t;
    e;
    n;
    x5c;
    alg;
    constructor(data) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) this[property] = data[property];
        }
      }
      if (!data) {
        this.x5c = [];
      }
    }
    init(_data) {
      if (_data) {
        this.kty = _data["kty"];
        this.use = _data["use"];
        this.kid = _data["kid"];
        this.x5t = _data["x5t"];
        this.e = _data["e"];
        this.n = _data["n"];
        if (Array.isArray(_data["x5c"])) {
          this.x5c = [];
          for (let item of _data["x5c"]) this.x5c.push(item);
        }
        this.alg = _data["alg"];
      }
    }
    static fromJS(data) {
      data = typeof data === "object" ? data : {};
      let result = new JsonWebKey();
      result.init(data);
      return result;
    }
    toJSON(data) {
      data = typeof data === "object" ? data : {};
      data["kty"] = this.kty;
      data["use"] = this.use;
      data["kid"] = this.kid;
      data["x5t"] = this.x5t;
      data["e"] = this.e;
      data["n"] = this.n;
      if (Array.isArray(this.x5c)) {
        data["x5c"] = [];
        for (let item of this.x5c) data["x5c"].push(item);
      }
      data["alg"] = this.alg;
      return data;
    }
  }
  GaAuthApiClient.JsonWebKey = JsonWebKey;
  class ApiException extends Error {
    message;
    status;
    response;
    headers;
    result;
    constructor(message, status, response, headers, result) {
      super();
      this.message = message;
      this.status = status;
      this.response = response;
      this.headers = headers;
      this.result = result;
    }
    isApiException = true;
    static isApiException(obj) {
      return obj.isApiException === true;
    }
  }
  GaAuthApiClient.ApiException = ApiException;
  function throwException(message, status, response, headers, result) {
    if (result !== null && result !== undefined) return throwError(result);else return throwError(new ApiException(message, status, response, headers, null));
  }
  function blobToText(blob) {
    return new Observable(observer => {
      if (!blob) {
        observer.next("");
        observer.complete();
      } else {
        let reader = new FileReader();
        reader.onload = event => {
          observer.next(event.target.result);
          observer.complete();
        };
        reader.readAsText(blob);
      }
    });
  }
})(GaAuthApiClient || (GaAuthApiClient = {}));
let GaAuthService = /*#__PURE__*/(() => {
  class GaAuthService {
    gaAuthStore;
    constructor(gaAuthStore) {
      this.gaAuthStore = gaAuthStore;
    }
    getAccessTokenUserInfo() {
      const token = this.gaAuthStore.accessToken;
      if (!token) {
        return null;
      }
      const claims = decodeJwt(token);
      return {
        portalId: claims.unique_name,
        givenName: claims.given_name,
        familyName: claims.family_name,
        email: claims.email,
        roles: claims.ga_role ? Array.isArray(claims.ga_role) ? claims.ga_role : [claims.ga_role] : []
      };
    }
    static ɵfac = function GaAuthService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GaAuthService)(i0.ɵɵinject(GaAuthStore));
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: GaAuthService,
      factory: GaAuthService.ɵfac
    });
  }
  return GaAuthService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class GaAuthApiAuthService extends GaAuthService {
  authStore;
  accessTokenService;
  samlRedirectService;
  config;
  cookieService;
  logoutService;
  constructor(authStore, accessTokenService, samlRedirectService, config, cookieService, logoutService) {
    super(authStore);
    this.authStore = authStore;
    this.accessTokenService = accessTokenService;
    this.samlRedirectService = samlRedirectService;
    this.config = config;
    this.cookieService = cookieService;
    this.logoutService = logoutService;
  }
  _isSessionTimedOut$ = new BehaviorSubject(false);
  get isSessionTimedOut$() {
    return this._isSessionTimedOut$.asObservable();
  }
  get isAuthorized$() {
    return this.refreshTokenIsExpired$().pipe(map(isExp => !isExp));
  }
  get authEnabled() {
    return !this.config.devOptions?.disableAuth;
  }
  async makeSamlRequest(saml) {
    await this.samlRedirectService.handleSamlRedirect(saml).pipe(finalize(() => {
      this.authStore.cookies$.next(document.cookie);
    }), catchError(err => {
      console.error("Error making SAML request", err);
      throw err;
    })).toPromise();
  }
  async getAccessToken(options) {
    if (!this.authEnabled) {
      return "fake access token";
    }
    if (isJwtValid(this.authStore.accessToken)) {
      return this.authStore.accessToken;
    }
    if (this.refreshTokenIsExpired()) {
      this._isSessionTimedOut$.next(true);
      const isSessionTimedOut = await this.isSessionTimedOut$.pipe(first(x => !x)).toPromise();
      if (isSessionTimedOut === false) {
        return this.requestNewAccessToken(options?.requestedRoles || []);
      } else {
        throw new Error("user logged out");
      }
    }
    const accessToken = await this.requestNewAccessToken(options?.requestedRoles || []);
    if (this.authStore.reauthenticating) {
      this.authStore.setReauthenticating(false);
    }
    return accessToken;
  }
  async logout() {
    this.authStore.accessToken$.next(null);
    await this.logoutService.logout(new GaAuthApiClient.LogoutInput({
      logoutOfAllSessions: false
    })).toPromise();
    this.cookieService.clearTokenCookies();
    this.authStore.cookies$.next(document.cookie);
  }
  reauthenticate(callback) {
    this.authStore.setReauthenticating(true);
    const listenForReauthenticating = setInterval(() => {
      if (!this.authStore.reauthenticating) {
        clearInterval(listenForReauthenticating);
        this._isSessionTimedOut$.next(false);
        this.authStore.cookies$.next(document.cookie);
        callback();
      }
    }, 500);
  }
  async requestNewAccessToken(requestedRoles) {
    const res = await this.accessTokenService.getAccessTokenForWeb(new GaAuthApiClient.GetAccessTokenForWebRequest({
      requestedRoles
    })).pipe(retry(2), finalize(() => {
      this.authStore.cookies$.next(document.cookie);
    }), tap(({
      accessToken
    }) => {
      this.authStore.accessToken$.next(accessToken);
    }), catchError(err => {
      console.error(err);
      this.cookieService.clearTokenCookies();
      this.authStore.cookies$.next(document.cookie);
      return throwError(() => new Error("Unable to get new access token"));
    })).toPromise();
    await this.accessTokenService.ackWebAccessToken(new GaAuthApiClient.AckWebAccessTokenRequest({
      ackToken: res.ackToken
    })).pipe(catchError(err => {
      console.warn(err);
      return of("");
    })).toPromise();
    return res.accessToken;
  }
  refreshTokenIsExpired() {
    const expDate = this.authStore.refreshTokenExpirationDate$.getValue();
    if (expDate == null) return true;
    return expDate < Date.now();
  }
  refreshTokenIsExpired$() {
    return this.authStore.refreshTokenExpirationDate$.asObservable().pipe(map(expDate => {
      if (expDate == null) return true;
      return expDate < Date.now();
    }));
  }
}
let GaAuthDevApiAuthStore = /*#__PURE__*/(() => {
  class GaAuthDevApiAuthStore extends GaAuthStore {
    refreshTokenExpirationDate$ = new BehaviorSubject(null);
    constructor() {
      super();
      this.expirationDateFromLocalStorage$.subscribe(this.refreshTokenExpirationDate$);
    }
    _refreshToken$ = new BehaviorSubject(localStorage.getItem("ga_refresh_token"));
    get refreshToken$() {
      return this._refreshToken$.asObservable();
    }
    _reauthenticating$ = new BehaviorSubject(localStorage.getItem("ga_reauthenticating") === "true");
    get reauthenticating$() {
      return this._reauthenticating$.asObservable();
    }
    get reauthenticating() {
      return localStorage.getItem("ga_reauthenticating") === "true";
    }
    get refreshToken() {
      return localStorage.getItem("ga_refresh_token");
    }
    get expirationDateFromLocalStorage$() {
      return this._refreshToken$.asObservable().pipe(map(refreshToken => {
        if (!refreshToken) return null;
        return getExpirationDateFromJwt(refreshToken) * 1000;
      }));
    }
    setReauthenticating(value) {
      localStorage.setItem("ga_reauthenticating", `${value}`);
      this._reauthenticating$.next(value);
    }
    setRefreshToken(newRefreshToken) {
      localStorage.setItem("ga_refresh_token", newRefreshToken || "");
      this._refreshToken$.next(newRefreshToken);
    }
    resyncRefreshToken() {
      this._refreshToken$.next(localStorage.getItem("ga_refresh_token"));
    }
    static ɵfac = function GaAuthDevApiAuthStore_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GaAuthDevApiAuthStore)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: GaAuthDevApiAuthStore,
      factory: GaAuthDevApiAuthStore.ɵfac
    });
  }
  return GaAuthDevApiAuthStore;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * This class allows the cas dev api client to access the dev auth service through the api base class since you can't directly inject the auth service
 */
let GaAuthDevApiConfig = /*#__PURE__*/(() => {
  class GaAuthDevApiConfig {
    authStore;
    constructor(authStore) {
      this.authStore = authStore;
    }
    get refreshToken() {
      return this.authStore.refreshToken;
    }
    static ɵfac = function GaAuthDevApiConfig_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GaAuthDevApiConfig)(i0.ɵɵinject(GaAuthDevApiAuthStore));
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: GaAuthDevApiConfig,
      factory: GaAuthDevApiConfig.ɵfac
    });
  }
  return GaAuthDevApiConfig;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

//----------------------
// <auto-generated>
//     Generated using the NSwag toolchain v13.18.2.0 (NJsonSchema v10.8.0.0 (Newtonsoft.Json v13.0.0.0)) (http://NSwag.org)
// </auto-generated>
//----------------------
/* tslint:disable */
/* eslint-disable */
// ReSharper disable InconsistentNaming
var GaAuthDevApiClient;
(function (GaAuthDevApiClient) {
  GaAuthDevApiClient.GA_AUTH_DEV_API_BASE_URL = new InjectionToken("GA_AUTH_DEV_API_BASE_URL");
  class GaAuthDevApiClientBase {
    apiConfig;
    constructor(apiConfig) {
      this.apiConfig = apiConfig;
    }
    transformOptions(options) {
      const refreshToken = this.apiConfig.refreshToken;
      if (refreshToken != null) {
        options.headers = options.headers.append("authorization", `Bearer ${refreshToken}`);
      }
      return Promise.resolve(options);
    }
  }
  GaAuthDevApiClient.GaAuthDevApiClientBase = GaAuthDevApiClientBase;
  let AccessTokenService = /*#__PURE__*/(() => {
    class AccessTokenService extends GaAuthDevApiClientBase {
      http;
      baseUrl;
      jsonParseReviver = undefined;
      constructor(configuration, http, baseUrl) {
        super(configuration);
        this.http = http;
        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
      }
      getAccessToken(forWebRequest, httpContext) {
        let url_ = this.baseUrl + "/auth/web/AccessToken";
        url_ = url_.replace(/[?&]$/, "");
        const content_ = JSON.stringify(forWebRequest);
        let options_ = {
          body: content_,
          observe: "response",
          responseType: "blob",
          context: httpContext,
          headers: new HttpHeaders({
            "Content-Type": "application/json",
            Accept: "application/json"
          })
        };
        return from(this.transformOptions(options_)).pipe(mergeMap(transformedOptions_ => {
          return this.http.request("post", url_, transformedOptions_);
        })).pipe(mergeMap(response_ => {
          return this.processGetAccessToken(response_);
        })).pipe(catchError(response_ => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAccessToken(response_);
            } catch (e) {
              return throwError(e);
            }
          } else return throwError(response_);
        }));
      }
      processGetAccessToken(response) {
        const status = response.status;
        const responseBlob = response instanceof HttpResponse ? response.body : response.error instanceof Blob ? response.error : undefined;
        let _headers = {};
        if (response.headers) {
          for (let key of response.headers.keys()) {
            _headers[key] = response.headers.get(key);
          }
        }
        if (status === 200) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result200 = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = GetAccessTokenResponse.fromJS(resultData200);
            return of(result200);
          }));
        } else if (status === 401) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result401 = null;
            let resultData401 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result401 = CentralAuthErrorResponse.fromJS(resultData401);
            return throwException("A server side error occurred.", status, _responseText, _headers, result401);
          }));
        } else if (status === 403) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result403 = null;
            let resultData403 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result403 = CentralAuthErrorResponse.fromJS(resultData403);
            return throwException("A server side error occurred.", status, _responseText, _headers, result403);
          }));
        } else if (status === 500) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result500 = null;
            let resultData500 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result500 = CentralAuthErrorResponse.fromJS(resultData500);
            return throwException("A server side error occurred.", status, _responseText, _headers, result500);
          }));
        } else if (status !== 200 && status !== 204) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
          }));
        }
        return of(null);
      }
      ackWebAccessToken(request, httpContext) {
        let url_ = this.baseUrl + "/auth/web/Ack";
        url_ = url_.replace(/[?&]$/, "");
        const content_ = JSON.stringify(request);
        let options_ = {
          body: content_,
          observe: "response",
          responseType: "blob",
          context: httpContext,
          headers: new HttpHeaders({
            "Content-Type": "application/json"
          })
        };
        return from(this.transformOptions(options_)).pipe(mergeMap(transformedOptions_ => {
          return this.http.request("post", url_, transformedOptions_);
        })).pipe(mergeMap(response_ => {
          return this.processAckWebAccessToken(response_);
        })).pipe(catchError(response_ => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processAckWebAccessToken(response_);
            } catch (e) {
              return throwError(e);
            }
          } else return throwError(response_);
        }));
      }
      processAckWebAccessToken(response) {
        const status = response.status;
        const responseBlob = response instanceof HttpResponse ? response.body : response.error instanceof Blob ? response.error : undefined;
        let _headers = {};
        if (response.headers) {
          for (let key of response.headers.keys()) {
            _headers[key] = response.headers.get(key);
          }
        }
        if (status === 204) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            return of(null);
          }));
        } else if (status !== 200 && status !== 204) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
          }));
        }
        return of(null);
      }
      static ɵfac = function AccessTokenService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AccessTokenService)(i0.ɵɵinject(GaAuthDevApiConfig), i0.ɵɵinject(HttpClient), i0.ɵɵinject(GaAuthDevApiClient.GA_AUTH_DEV_API_BASE_URL, 8));
      };
      static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AccessTokenService,
        factory: AccessTokenService.ɵfac
      });
    }
    return AccessTokenService;
  })();
  GaAuthDevApiClient.AccessTokenService = AccessTokenService;
  let LogoutService = /*#__PURE__*/(() => {
    class LogoutService extends GaAuthDevApiClientBase {
      http;
      baseUrl;
      jsonParseReviver = undefined;
      constructor(configuration, http, baseUrl) {
        super(configuration);
        this.http = http;
        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
      }
      logout(input, httpContext) {
        let url_ = this.baseUrl + "/auth/Logout";
        url_ = url_.replace(/[?&]$/, "");
        const content_ = JSON.stringify(input);
        let options_ = {
          body: content_,
          observe: "response",
          responseType: "blob",
          context: httpContext,
          headers: new HttpHeaders({
            "Content-Type": "application/json"
          })
        };
        return from(this.transformOptions(options_)).pipe(mergeMap(transformedOptions_ => {
          return this.http.request("post", url_, transformedOptions_);
        })).pipe(mergeMap(response_ => {
          return this.processLogout(response_);
        })).pipe(catchError(response_ => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processLogout(response_);
            } catch (e) {
              return throwError(e);
            }
          } else return throwError(response_);
        }));
      }
      processLogout(response) {
        const status = response.status;
        const responseBlob = response instanceof HttpResponse ? response.body : response.error instanceof Blob ? response.error : undefined;
        let _headers = {};
        if (response.headers) {
          for (let key of response.headers.keys()) {
            _headers[key] = response.headers.get(key);
          }
        }
        if (status === 204) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            return of(null);
          }));
        } else if (status === 401) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result401 = null;
            let resultData401 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result401 = CentralAuthErrorResponse.fromJS(resultData401);
            return throwException("A server side error occurred.", status, _responseText, _headers, result401);
          }));
        } else if (status === 403) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result403 = null;
            let resultData403 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result403 = CentralAuthErrorResponse.fromJS(resultData403);
            return throwException("A server side error occurred.", status, _responseText, _headers, result403);
          }));
        } else if (status === 500) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result500 = null;
            let resultData500 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result500 = CentralAuthErrorResponse.fromJS(resultData500);
            return throwException("A server side error occurred.", status, _responseText, _headers, result500);
          }));
        } else if (status !== 200 && status !== 204) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
          }));
        }
        return of(null);
      }
      static ɵfac = function LogoutService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LogoutService)(i0.ɵɵinject(GaAuthDevApiConfig), i0.ɵɵinject(HttpClient), i0.ɵɵinject(GaAuthDevApiClient.GA_AUTH_DEV_API_BASE_URL, 8));
      };
      static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LogoutService,
        factory: LogoutService.ɵfac
      });
    }
    return LogoutService;
  })();
  GaAuthDevApiClient.LogoutService = LogoutService;
  let PublicKeysService = /*#__PURE__*/(() => {
    class PublicKeysService extends GaAuthDevApiClientBase {
      http;
      baseUrl;
      jsonParseReviver = undefined;
      constructor(configuration, http, baseUrl) {
        super(configuration);
        this.http = http;
        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
      }
      getPublicKeys(httpContext) {
        let url_ = this.baseUrl + "/auth/PublicKeys";
        url_ = url_.replace(/[?&]$/, "");
        let options_ = {
          observe: "response",
          responseType: "blob",
          context: httpContext,
          headers: new HttpHeaders({
            Accept: "application/json"
          })
        };
        return from(this.transformOptions(options_)).pipe(mergeMap(transformedOptions_ => {
          return this.http.request("get", url_, transformedOptions_);
        })).pipe(mergeMap(response_ => {
          return this.processGetPublicKeys(response_);
        })).pipe(catchError(response_ => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetPublicKeys(response_);
            } catch (e) {
              return throwError(e);
            }
          } else return throwError(response_);
        }));
      }
      processGetPublicKeys(response) {
        const status = response.status;
        const responseBlob = response instanceof HttpResponse ? response.body : response.error instanceof Blob ? response.error : undefined;
        let _headers = {};
        if (response.headers) {
          for (let key of response.headers.keys()) {
            _headers[key] = response.headers.get(key);
          }
        }
        if (status === 200) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result200 = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = JsonWebKeySet.fromJS(resultData200);
            return of(result200);
          }));
        } else if (status !== 200 && status !== 204) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
          }));
        }
        return of(null);
      }
      static ɵfac = function PublicKeysService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicKeysService)(i0.ɵɵinject(GaAuthDevApiConfig), i0.ɵɵinject(HttpClient), i0.ɵɵinject(GaAuthDevApiClient.GA_AUTH_DEV_API_BASE_URL, 8));
      };
      static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicKeysService,
        factory: PublicKeysService.ɵfac
      });
    }
    return PublicKeysService;
  })();
  GaAuthDevApiClient.PublicKeysService = PublicKeysService;
  let SamlRedirectService = /*#__PURE__*/(() => {
    class SamlRedirectService extends GaAuthDevApiClientBase {
      http;
      baseUrl;
      jsonParseReviver = undefined;
      constructor(configuration, http, baseUrl) {
        super(configuration);
        this.http = http;
        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
      }
      handleSamlRedirect(samlResponse, httpContext) {
        let url_ = this.baseUrl + "/auth/SamlRedirect";
        url_ = url_.replace(/[?&]$/, "");
        let content_ = "";
        if (samlResponse !== undefined) content_ += encodeURIComponent("SamlResponse") + "=" + encodeURIComponent("" + samlResponse) + "&";
        content_ = content_.replace(/&$/, "");
        let options_ = {
          body: content_,
          observe: "response",
          responseType: "blob",
          context: httpContext,
          headers: new HttpHeaders({
            "Content-Type": "application/x-www-form-urlencoded",
            Accept: "application/json"
          })
        };
        return from(this.transformOptions(options_)).pipe(mergeMap(transformedOptions_ => {
          return this.http.request("post", url_, transformedOptions_);
        })).pipe(mergeMap(response_ => {
          return this.processHandleSamlRedirect(response_);
        })).pipe(catchError(response_ => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processHandleSamlRedirect(response_);
            } catch (e) {
              return throwError(e);
            }
          } else return throwError(response_);
        }));
      }
      processHandleSamlRedirect(response) {
        const status = response.status;
        const responseBlob = response instanceof HttpResponse ? response.body : response.error instanceof Blob ? response.error : undefined;
        let _headers = {};
        if (response.headers) {
          for (let key of response.headers.keys()) {
            _headers[key] = response.headers.get(key);
          }
        }
        if (status === 200) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result200 = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = SamlRedirectResponse.fromJS(resultData200);
            return of(result200);
          }));
        } else if (status === 403) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result403 = null;
            let resultData403 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result403 = CentralAuthErrorResponse.fromJS(resultData403);
            return throwException("A server side error occurred.", status, _responseText, _headers, result403);
          }));
        } else if (status === 500) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            let result500 = null;
            let resultData500 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result500 = CentralAuthErrorResponse.fromJS(resultData500);
            return throwException("A server side error occurred.", status, _responseText, _headers, result500);
          }));
        } else if (status !== 200 && status !== 204) {
          return blobToText(responseBlob).pipe(mergeMap(_responseText => {
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
          }));
        }
        return of(null);
      }
      static ɵfac = function SamlRedirectService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SamlRedirectService)(i0.ɵɵinject(GaAuthDevApiConfig), i0.ɵɵinject(HttpClient), i0.ɵɵinject(GaAuthDevApiClient.GA_AUTH_DEV_API_BASE_URL, 8));
      };
      static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SamlRedirectService,
        factory: SamlRedirectService.ɵfac
      });
    }
    return SamlRedirectService;
  })();
  GaAuthDevApiClient.SamlRedirectService = SamlRedirectService;
  class GetAccessTokenResponse {
    accessToken;
    refreshToken;
    ackToken;
    constructor(data) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) this[property] = data[property];
        }
      }
    }
    init(_data) {
      if (_data) {
        this.accessToken = _data["accessToken"];
        this.refreshToken = _data["refreshToken"];
        this.ackToken = _data["ackToken"];
      }
    }
    static fromJS(data) {
      data = typeof data === "object" ? data : {};
      let result = new GetAccessTokenResponse();
      result.init(data);
      return result;
    }
    toJSON(data) {
      data = typeof data === "object" ? data : {};
      data["accessToken"] = this.accessToken;
      data["refreshToken"] = this.refreshToken;
      data["ackToken"] = this.ackToken;
      return data;
    }
  }
  GaAuthDevApiClient.GetAccessTokenResponse = GetAccessTokenResponse;
  class CentralAuthErrorResponse {
    message;
    constructor(data) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) this[property] = data[property];
        }
      }
    }
    init(_data) {
      if (_data) {
        this.message = _data["message"];
      }
    }
    static fromJS(data) {
      data = typeof data === "object" ? data : {};
      let result = new CentralAuthErrorResponse();
      result.init(data);
      return result;
    }
    toJSON(data) {
      data = typeof data === "object" ? data : {};
      data["message"] = this.message;
      return data;
    }
  }
  GaAuthDevApiClient.CentralAuthErrorResponse = CentralAuthErrorResponse;
  class GetAccessTokenForWebRequest {
    requestedRoles;
    constructor(data) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) this[property] = data[property];
        }
      }
      if (!data) {
        this.requestedRoles = [];
      }
    }
    init(_data) {
      if (_data) {
        if (Array.isArray(_data["requestedRoles"])) {
          this.requestedRoles = [];
          for (let item of _data["requestedRoles"]) this.requestedRoles.push(item);
        }
      }
    }
    static fromJS(data) {
      data = typeof data === "object" ? data : {};
      let result = new GetAccessTokenForWebRequest();
      result.init(data);
      return result;
    }
    toJSON(data) {
      data = typeof data === "object" ? data : {};
      if (Array.isArray(this.requestedRoles)) {
        data["requestedRoles"] = [];
        for (let item of this.requestedRoles) data["requestedRoles"].push(item);
      }
      return data;
    }
  }
  GaAuthDevApiClient.GetAccessTokenForWebRequest = GetAccessTokenForWebRequest;
  class AckWebAccessTokenRequest {
    ackToken;
    constructor(data) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) this[property] = data[property];
        }
      }
    }
    init(_data) {
      if (_data) {
        this.ackToken = _data["ackToken"];
      }
    }
    static fromJS(data) {
      data = typeof data === "object" ? data : {};
      let result = new AckWebAccessTokenRequest();
      result.init(data);
      return result;
    }
    toJSON(data) {
      data = typeof data === "object" ? data : {};
      data["ackToken"] = this.ackToken;
      return data;
    }
  }
  GaAuthDevApiClient.AckWebAccessTokenRequest = AckWebAccessTokenRequest;
  class LogoutInput {
    logoutOfAllSessions;
    constructor(data) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) this[property] = data[property];
        }
      }
    }
    init(_data) {
      if (_data) {
        this.logoutOfAllSessions = _data["logoutOfAllSessions"];
      }
    }
    static fromJS(data) {
      data = typeof data === "object" ? data : {};
      let result = new LogoutInput();
      result.init(data);
      return result;
    }
    toJSON(data) {
      data = typeof data === "object" ? data : {};
      data["logoutOfAllSessions"] = this.logoutOfAllSessions;
      return data;
    }
  }
  GaAuthDevApiClient.LogoutInput = LogoutInput;
  class JsonWebKeySet {
    keys;
    constructor(data) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) this[property] = data[property];
        }
      }
      if (!data) {
        this.keys = [];
      }
    }
    init(_data) {
      if (_data) {
        if (Array.isArray(_data["keys"])) {
          this.keys = [];
          for (let item of _data["keys"]) this.keys.push(JsonWebKey.fromJS(item));
        }
      }
    }
    static fromJS(data) {
      data = typeof data === "object" ? data : {};
      let result = new JsonWebKeySet();
      result.init(data);
      return result;
    }
    toJSON(data) {
      data = typeof data === "object" ? data : {};
      if (Array.isArray(this.keys)) {
        data["keys"] = [];
        for (let item of this.keys) data["keys"].push(item.toJSON());
      }
      return data;
    }
  }
  GaAuthDevApiClient.JsonWebKeySet = JsonWebKeySet;
  class JsonWebKey {
    kty;
    use;
    kid;
    x5t;
    e;
    n;
    x5c;
    alg;
    constructor(data) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) this[property] = data[property];
        }
      }
      if (!data) {
        this.x5c = [];
      }
    }
    init(_data) {
      if (_data) {
        this.kty = _data["kty"];
        this.use = _data["use"];
        this.kid = _data["kid"];
        this.x5t = _data["x5t"];
        this.e = _data["e"];
        this.n = _data["n"];
        if (Array.isArray(_data["x5c"])) {
          this.x5c = [];
          for (let item of _data["x5c"]) this.x5c.push(item);
        }
        this.alg = _data["alg"];
      }
    }
    static fromJS(data) {
      data = typeof data === "object" ? data : {};
      let result = new JsonWebKey();
      result.init(data);
      return result;
    }
    toJSON(data) {
      data = typeof data === "object" ? data : {};
      data["kty"] = this.kty;
      data["use"] = this.use;
      data["kid"] = this.kid;
      data["x5t"] = this.x5t;
      data["e"] = this.e;
      data["n"] = this.n;
      if (Array.isArray(this.x5c)) {
        data["x5c"] = [];
        for (let item of this.x5c) data["x5c"].push(item);
      }
      data["alg"] = this.alg;
      return data;
    }
  }
  GaAuthDevApiClient.JsonWebKey = JsonWebKey;
  class SamlRedirectResponse {
    refreshToken;
    constructor(data) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) this[property] = data[property];
        }
      }
    }
    init(_data) {
      if (_data) {
        this.refreshToken = _data["refreshToken"];
      }
    }
    static fromJS(data) {
      data = typeof data === "object" ? data : {};
      let result = new SamlRedirectResponse();
      result.init(data);
      return result;
    }
    toJSON(data) {
      data = typeof data === "object" ? data : {};
      data["refreshToken"] = this.refreshToken;
      return data;
    }
  }
  GaAuthDevApiClient.SamlRedirectResponse = SamlRedirectResponse;
  class ApiException extends Error {
    message;
    status;
    response;
    headers;
    result;
    constructor(message, status, response, headers, result) {
      super();
      this.message = message;
      this.status = status;
      this.response = response;
      this.headers = headers;
      this.result = result;
    }
    isApiException = true;
    static isApiException(obj) {
      return obj.isApiException === true;
    }
  }
  GaAuthDevApiClient.ApiException = ApiException;
  function throwException(message, status, response, headers, result) {
    if (result !== null && result !== undefined) return throwError(result);else return throwError(new ApiException(message, status, response, headers, null));
  }
  function blobToText(blob) {
    return new Observable(observer => {
      if (!blob) {
        observer.next("");
        observer.complete();
      } else {
        let reader = new FileReader();
        reader.onload = event => {
          observer.next(event.target.result);
          observer.complete();
        };
        reader.readAsText(blob);
      }
    });
  }
})(GaAuthDevApiClient || (GaAuthDevApiClient = {}));
class GaAuthDevApiAuthService extends GaAuthService {
  authStore;
  samlRedirectService;
  accessTokenService;
  config;
  logoutService;
  constructor(authStore, samlRedirectService, accessTokenService, config, logoutService) {
    super(authStore);
    this.authStore = authStore;
    this.samlRedirectService = samlRedirectService;
    this.accessTokenService = accessTokenService;
    this.config = config;
    this.logoutService = logoutService;
  }
  _isSessionTimedOut$ = new BehaviorSubject(false);
  get isSessionTimedOut$() {
    return this._isSessionTimedOut$.asObservable();
  }
  get isAuthorized$() {
    return this.refreshTokenIsExpired$().pipe(map(isExp => !isExp));
  }
  get authEnabled() {
    return !this.config.devOptions?.disableAuth;
  }
  async makeSamlRequest(saml) {
    if (!this.authEnabled) {
      return Promise.resolve();
    }
    await this.samlRedirectService.handleSamlRedirect(saml).pipe(tap(({
      refreshToken
    }) => {
      this.authStore.setRefreshToken(refreshToken);
    }), catchError(err => {
      console.error(err);
      return of("");
    })).toPromise();
  }
  async getAccessToken(options) {
    if (!this.authEnabled) {
      return Promise.resolve("fake access token");
    }
    if (isJwtValid(this.authStore.accessToken)) {
      return this.authStore.accessToken;
    }
    if (this.refreshTokenIsExpired()) {
      this._isSessionTimedOut$.next(true);
      const isSessionTimedOut = await this.isSessionTimedOut$.pipe(first(x => !x)).toPromise();
      if (isSessionTimedOut === false) {
        return this.requestNewAccessToken(options?.requestedRoles || []);
      } else {
        throw new Error("user logged out");
      }
    }
    const accessToken = await this.requestNewAccessToken(options?.requestedRoles || []);
    if (this.authStore.reauthenticating) {
      this.authStore.setReauthenticating(false);
    }
    return accessToken;
  }
  async logout() {
    this.authStore.accessToken$.next(null);
    this.authStore.setRefreshToken("");
    await this.logoutService.logout(new GaAuthDevApiClient.LogoutInput({
      logoutOfAllSessions: false
    })).toPromise();
  }
  reauthenticate(callback) {
    this.authStore.setReauthenticating(true);
    const listenForReauthenticating = setInterval(() => {
      if (!this.authStore.reauthenticating) {
        clearInterval(listenForReauthenticating);
        this._isSessionTimedOut$.next(false);
        this.authStore.resyncRefreshToken();
        callback();
      }
    }, 500);
  }
  async requestNewAccessToken(requestedRoles) {
    const res = await this.accessTokenService.getAccessToken(new GaAuthDevApiClient.GetAccessTokenForWebRequest({
      requestedRoles
    })).pipe(retry(2), tap(({
      accessToken,
      refreshToken
    }) => {
      this.authStore.accessToken$.next(accessToken);
      this.authStore.setRefreshToken(refreshToken);
    }), catchError(err => {
      console.error(err);
      return throwError(() => new Error("Unable to get ew access token"));
    })).toPromise();
    await this.accessTokenService.ackWebAccessToken(new GaAuthApiClient.AckWebAccessTokenRequest({
      ackToken: res.ackToken
    })).toPromise();
    return res.accessToken;
  }
  refreshTokenIsExpired() {
    const expDate = this.authStore.refreshTokenExpirationDate$.getValue();
    if (expDate == null) return true;
    return expDate < Date.now() / 1000;
  }
  refreshTokenIsExpired$() {
    return this.authStore.refreshTokenExpirationDate$.asObservable().pipe(map(expDate => {
      if (expDate == null) return true;
      return expDate < Date.now() / 1000;
    }));
  }
}
let GaAuthApiClientModule = /*#__PURE__*/(() => {
  class GaAuthApiClientModule {
    static ɵfac = function GaAuthApiClientModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GaAuthApiClientModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: GaAuthApiClientModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [GaAuthApiClient.AccessTokenService, GaAuthApiClient.SamlRedirectService, GaAuthApiClient.LogoutService, provideHttpClient(withInterceptorsFromDi())]
    });
  }
  return GaAuthApiClientModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let GaAuthDevApiClientModule = /*#__PURE__*/(() => {
  class GaAuthDevApiClientModule {
    static ɵfac = function GaAuthDevApiClientModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GaAuthDevApiClientModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: GaAuthDevApiClientModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [GaAuthDevApiClient.AccessTokenService, GaAuthDevApiClient.SamlRedirectService, GaAuthDevApiClient.LogoutService, provideHttpClient(withInterceptorsFromDi())]
    });
  }
  return GaAuthDevApiClientModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function getAuthServiceImplementation(devApiAuthStore, apiAuthStore, devSamlService, devAccessTokenService, devLogoutService, accessTokenService, samlService, logoutService, config, cookieService) {
  if (config.devOptions?.useDevApi) {
    return new GaAuthDevApiAuthService(devApiAuthStore, devSamlService, devAccessTokenService, config, devLogoutService);
  }
  return new GaAuthApiAuthService(apiAuthStore, accessTokenService, samlService, config, cookieService, logoutService);
}
function getAuthApiBaseUrl(config) {
  return config.gaAuthApiUrl;
}
let GaAuthModule = /*#__PURE__*/(() => {
  class GaAuthModule {
    static ɵfac = function GaAuthModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GaAuthModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: GaAuthModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [{
        provide: GaAuthService,
        useFactory: getAuthServiceImplementation,
        deps: [GaAuthDevApiAuthStore, GaAuthApiAuthStore, GaAuthDevApiClient.SamlRedirectService, GaAuthDevApiClient.AccessTokenService, GaAuthDevApiClient.LogoutService, GaAuthApiClient.AccessTokenService, GaAuthApiClient.SamlRedirectService, GaAuthApiClient.LogoutService, GA_AUTH_CONFIG, GaAuthCookieService]
      }, {
        provide: GaAuthDevApiConfig,
        useClass: GaAuthDevApiConfig,
        deps: [GaAuthDevApiAuthStore]
      }, {
        provide: GaAuthApiConfig,
        useClass: GaAuthApiConfig,
        deps: [GaAuthApiAuthStore]
      }, {
        provide: GaAuthDevApiAuthStore,
        useClass: GaAuthDevApiAuthStore,
        deps: []
      }, {
        provide: GaAuthApiAuthStore,
        useClass: GaAuthApiAuthStore,
        deps: [GaAuthCookieService]
      }, {
        provide: GaAuthCookieService,
        useClass: GaAuthCookieService,
        deps: [GA_AUTH_CONFIG]
      }, {
        provide: GaAuthApiClient.GA_AUTH_API_BASE_URL,
        useFactory: getAuthApiBaseUrl,
        deps: [GA_AUTH_CONFIG]
      }, {
        provide: GaAuthDevApiClient.GA_AUTH_DEV_API_BASE_URL,
        useFactory: getAuthApiBaseUrl,
        deps: [GA_AUTH_CONFIG]
      }],
      imports: [GaAuthApiClientModule, GaAuthDevApiClientModule]
    });
  }
  return GaAuthModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of central-auth-angular
 */

/**
 * Generated bundle index. Do not edit.
 */

export { GA_AUTH_CONFIG, GaAuthModule, GaAuthService };
